import { Controller } from "@hotwired/stimulus";

// Linear-style zoom: the picture grows from where it sits into the centre of
// the viewport over a backdrop that fades the rest of the page out, and
// shrinks back on the next click, Escape, scroll or resize. Only a transform
// changes, so the page never reflows. With a `full` URL the original file is
// swapped in for the zoomed view so the enlarged picture stays sharp.
//
// The backdrop (and the close button inside it) is rendered in the markup;
// any click on it closes, the button is only there so people know they can.
export default class extends Controller {
  static targets = ["image", "backdrop"];
  static values = {
    full: String,
    margin: { type: Number, default: 24 },
    duration: { type: Number, default: 300 },
  };

  connect() {
    this.zoomOut = this.zoomOut.bind(this);
    this.onKeydown = (event) => {
      if (event.key === "Escape") this.zoomOut();
    };
  }

  disconnect() {
    if (!this.zoomed) return;
    this.zoomed = false;
    this.unlisten();
    this.imageTarget.style.transform = "";
    this.teardown();
  }

  toggle(event) {
    event.preventDefault();
    this.zoomed ? this.zoomOut() : this.zoomIn();
  }

  zoomIn() {
    clearTimeout(this.timer);
    const image = this.imageTarget;
    const rect = image.getBoundingClientRect();
    const scale = Math.max(
      1,
      Math.min(
        (window.innerWidth - 2 * this.marginValue) / rect.width,
        (window.innerHeight - 2 * this.marginValue) / rect.height
      )
    );
    const dx = window.innerWidth / 2 - (rect.left + rect.width / 2);
    const dy = window.innerHeight / 2 - (rect.top + rect.height / 2);

    this.backdropTarget.classList.remove("hidden");
    this.element.classList.remove("overflow-hidden");
    this.element.classList.add("relative", "z-50");

    image.classList.add("relative", "z-10", "cursor-zoom-out");
    image.classList.remove("cursor-zoom-in");
    image.setAttribute("aria-expanded", "true");
    image.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
    requestAnimationFrame(() => this.backdropTarget.classList.remove("opacity-0"));

    this.upgrade();
    this.zoomed = true;
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("scroll", this.zoomOut, { passive: true });
    window.addEventListener("resize", this.zoomOut);
  }

  zoomOut() {
    if (!this.zoomed) return;
    this.zoomed = false;
    this.unlisten();
    this.imageTarget.style.transform = "";
    this.backdropTarget.classList.add("opacity-0");
    // A timer rather than transitionend: reduced-motion users get no
    // transition, and the backdrop must still go away.
    this.timer = setTimeout(() => this.teardown(), this.durationValue);
  }

  unlisten() {
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("scroll", this.zoomOut);
    window.removeEventListener("resize", this.zoomOut);
  }

  teardown() {
    clearTimeout(this.timer);
    this.backdropTarget.classList.add("hidden");
    this.element.classList.add("overflow-hidden");
    this.element.classList.remove("relative", "z-50");
    const image = this.imageTarget;
    image.classList.remove("relative", "z-10", "cursor-zoom-out");
    image.classList.add("cursor-zoom-in");
    image.setAttribute("aria-expanded", "false");
  }

  // Swap the full-resolution file in once it has decoded, so the zoomed view
  // isn't the 800px variant blown up.
  upgrade() {
    if (!this.hasFullValue || this.upgraded) return;
    this.upgraded = true;
    const full = new Image();
    full.src = this.fullValue;
    full
      .decode()
      .then(() => {
        this.imageTarget.removeAttribute("srcset");
        this.imageTarget.src = this.fullValue;
      })
      .catch(() => {});
  }
}
